import React from "react";
import { PieChart, Pie, Cell } from "recharts";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import useMediaQuery from "@mui/material/useMediaQuery";
import "./StatusDonutCard.scss";

const RADIAN = Math.PI / 180;

// Leader line is a single straight run from the segment's true edge point
// (so it always touches the right color) out to the label — no artificial
// bend. A segment near the horizontal center axis reads as flat; one further
// off just has a gentle single slope.
const makeRenderOutsideLabel = (isCompact) => (props) => {
  const { cx, cy, midAngle, outerRadius, name, value, fill } = props;
  const cos = Math.cos(-RADIAN * midAngle);
  const sin = Math.sin(-RADIAN * midAngle);

  const sx = cx + outerRadius * cos;
  const sy = cy + outerRadius * sin;

  const isRight = cos >= 0;
  const lineLength = isCompact ? 20 : 40;
  const endX = isRight ? cx + outerRadius + lineLength : cx - outerRadius - lineLength;
  const textX = endX + (isRight ? 6 : -6);
  const textAnchor = isRight ? "start" : "end";
  const textY = sy;

  // One word per line — SVG <text> doesn't wrap on its own, so a long label
  // like "Disconnected Devices" would otherwise render as a single unbroken
  // line and run off the edge.
  const nameLines = name.split(" ");
  const nameLineHeight = isCompact ? 11 : 14;
  const nameGap = isCompact ? 5 : 8;

  return (
    <g>
      <path d={`M${sx},${sy}L${endX},${sy}`} stroke={fill} fill="none" />
      {nameLines.map((word, i) => (
        <text
          key={word}
          x={textX}
          y={textY - nameGap - (nameLines.length - 1 - i) * nameLineHeight}
          textAnchor={textAnchor}
          fill="#1a1a1a"
          fontFamily="Poppins"
          fontSize={isCompact ? 10 : 15}
          fontWeight={500}
        >
          {word}
        </text>
      ))}
      <text
        x={textX}
        y={textY + (isCompact ? 11 : 16)}
        textAnchor={textAnchor}
        fill="#7086FD"
        fontFamily="Poppins"
        fontSize={isCompact ? 12 : 18}
        fontWeight={600}
      >
        {value}
      </text>
    </g>
  );
};

function StatusDonutCard({ title, dateRangeDisplay, segments }) {
  const isCompact = useMediaQuery("(max-width:1536px)");
  const chartWidth = isCompact ? 270 : 520;
  const chartHeight = isCompact ? 160 : 260;
  const outerRadius = isCompact ? 40 : 75;
  const innerRadius = isCompact ? 25 : 50;

  return (
    <div className="status-donut-card">
      <div className="status-donut-header">
        <h3 className="status-donut-title">{title}</h3>
        {dateRangeDisplay && (
          <div className="status-donut-date">
            <CalendarMonthIcon className="calendar-icon" />
            {dateRangeDisplay}
          </div>
        )}
      </div>

      <div className="status-donut-body">
        <PieChart width={chartWidth} height={chartHeight}>
          <Pie
            data={segments}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            paddingAngle={0}
            labelLine={false}
            label={makeRenderOutsideLabel(isCompact)}
            isAnimationActive={false}
          >
            {segments.map((segment) => (
              <Cell key={segment.name} fill={segment.color} />
            ))}
          </Pie>
        </PieChart>
      </div>

      <div className="status-donut-legend">
        {segments.map((segment) => (
          <div className="legend-item" key={segment.name}>
            <span
              className="legend-swatch"
              style={{ backgroundColor: segment.color }}
            />
            {segment.legendLabel}
          </div>
        ))}
      </div>
    </div>
  );
}

export default StatusDonutCard;
